<template>
	<div class="header noprint">
		<!-- Logged in, mobile version -->
		<div v-if="loggedIn && isMobile">
			<div class="container-fluid d-flex">
				<!-- Logo -->
				<div>
					<RouterLink :to="{name: 'dashboard'}">
						<SvgLogo id="logo" class="light-mode-show" />
						<SvgLogoDark id="logo" class="dark-mode-show" />
					</RouterLink>
				</div>

				<div class="header-points-block-mobile">
					<div style="font-size: 14px">
						<PointsBlock v-if="t.headerPointsBlockText" :key="currentLanguage" :text-token-wrapper="t.headerPointsBlockText" />
					</div>
				</div>
				
				<!-- Mobile hamburger button -->
				<div class="mobile-menu-button" @click="openSideMenu()">
					<FontAwesomeIcon icon="bars" />

					<span v-if="menuBadge" class="badge-notification">{{ menuBadge }}</span>
				</div>
			</div>

			<!-- Swedbank custom: Display banners randomly based for mobile or web -->
			<div class="container-fluid">
				<responsive-image v-if="loggedIn" :height-proportion="topBanner.proportion" :image="topBanner.imageUrl" :style="{cursor: topBanner.url ? 'pointer': 'default'}" class="my-2" @click="open(topBanner.url)" />
			</div>

			<!-- Main menu goes separately below for logged in (mobile version) -->
			<transition-height name="fade" class="main-menu-transition-block container-fluid">
				<main-menu v-if="showMainMenu" />
			</transition-height>
		</div>

		<!-- Logged in, desktop version -->
		<div v-if="loggedIn && !isMobile" class="container d-flex">
			<div class="flex-grow-1">
				<!-- Logo -->
				<div>
					<RouterLink :to="{name: 'dashboard'}">
						<SvgLogo id="logo" class="light-mode-show" />
						<SvgLogoDark id="logo" class="dark-mode-show" />
					</RouterLink>
				</div>

				<!-- Main menu -->
				<transition-height name="fade" class="main-menu-transition-block">
					<main-menu v-if="showMainMenu" />
				</transition-height>
			</div>

			<!-- User block with logout button -->
			<div class="user-block">
				<div class="user d-flex mt-3">
					<RouterLink class="user-thumbnail mr-2" :to="{name: 'profile'}">
						<UserThumbnail :image-url="image" />
					</RouterLink>
					<div>
						<div class="user-title">
							{{ insertText(t.mainWelcomeText, firstName, lastName) }}
						</div>
						<!-- <div class="user-status">{{t.mainDefaultStatus}}</div> -->
						<div class="logout-button" @click.stop.prevent="logout()">
							<FontAwesomeIcon icon="sign-out-alt" class="mr-1" />
							
							<div class="logout-button-text">
								{{ t.mainLogOutButton }}
							</div>
						</div>
						<div class="clearfix" />
					</div>
				</div>
			</div>
		</div>

		<div v-if="!loggedIn" :class="{ 'container' : !isMobile, 'container-fluid' : isMobile }" class="d-flex">
			<!-- Logo -->
			<div class="vertical-align-flex" style="line-height: 0; padding-bottom: 10px;">
				<RouterLink :to="{name: 'sign-in'}">
					<SvgLogo id="logo" class="light-mode-show" />
					<SvgLogoDark id="logo" class="dark-mode-show" />
				</RouterLink>
			</div>

			<!-- Mobile hamburger button -->
			<div v-if="isMobile" class="mobile-menu-button flex-grow-1 text-right" @click="openSideMenu()">
				<FontAwesomeIcon icon="bars" />
			</div>

			<div v-if="!isMobile && showLoginButtons" class="vertical-align-flex flex-grow-1" style="align-items: end;">
				<div class="login-buttons d-flex">
					<div class="login-buttons-description hidden-md" v-html="t.headerLoginText" />

					<div class="login-buttons-buttons">
						<span>
							<button class="btn-login-customer" @click="location.href=customerLoginUrl">
								{{ t.headerLoginCustomerButton }}
							</button>
						</span>
						
						<span v-if="settings.registration_type=='phone'">
							<RouterLink v-slot="{ navigate }" :to="{name: 'phone-login', params: {language: currentLanguage}}" custom>
								<button class="btn-login-phone" @click="navigate">
									<FontAwesomeIcon icon="mobile-alt" /> {{ t.headerLoginMobileButton }}
								</button>
							</RouterLink>
						</span>
						
						<span v-if="settings.fbAppId">
							<button type="button" class="btn-login-facebook" @click="login('facebook')">
								<template v-if="connectionStatus.facebook=='connecting'">
									<FontAwesomeIcon icon="spinner" spin />
									{{ t.genericConnecting }}
								</template>
								<template v-else>
									<FontAwesomeIcon :icon="['fab', 'facebook-f']" />
									{{ t.loginSignInFbButton }}
								</template>
							</button>
						</span>
						
						<span v-if="settings.linkedinKey">
							<button type="button" class="btn-login-linkedin" @click="login('linkedin')">
								<template v-if="connectionStatus.linkedin=='connecting'">
									<FontAwesomeIcon icon="spinner" spin />
									{{ t.genericConnecting }}
								</template>
								<template v-else>
									<FontAwesomeIcon :icon="['fab', 'linkedin-in']" />
									Linkedin
								</template>
							</button>
						</span>
						
						<span v-if="settings.googleId">
							<button type="button" class="btn-login-google" @click="login('google')">
								<template v-if="connectionStatus.google=='connecting'">
									<FontAwesomeIcon icon="spinner" spin />
									{{ t.genericConnecting }}
								</template>
								<template v-else>
									<FontAwesomeIcon :icon="['fab', 'google']" />
									Google
								</template>
							</button>
						</span>

						<span v-if="settings.appleClientId">
							<button type="button" class="btn-login-apple" @click="login('apple')">
								<template v-if="connectionStatus.apple=='connecting'">
									<FontAwesomeIcon icon="spinner" spin />
									{{ t.genericConnecting }}
								</template>
								<template v-else>
									<FontAwesomeIcon :icon="['fab', 'apple']" />
									Apple
								</template>
							</button>
						</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Swedbank custom: Display banners randomly based for mobile or web -->
		<div v-if="!isMobile && loggedIn" class="pt-4" style="background-color: var(--default-background-color);">
			<div class="container">
				<responsive-image v-if="loggedIn" :height-proportion="topBanner.proportion" :image="topBanner.imageUrl" :style="{cursor: topBanner.url ? 'pointer': 'default'}" style="margin-top: 10px" @click="open(topBanner.url)" /> 
			</div>
		</div>

		<sidebar />
	</div>
</template>

<script setup>
import { computed } from 'vue'
import { includes } from "lodash-es"
import { useRoute, useRouter } from 'vue-router'
import Sidebar from './sidebar.vue'
import MainMenu from './main-menu.vue'
import { getRandomInt, insertText } from '../../includes/helpers'
import { personNameFormalize } from '../../classes/translations'
import SvgLogo from "@/svg/customer/images/logo.svg"
import SvgLogoDark from "@/svg/customer/images/logo-dark.svg"
import { logout } from '@/js/includes/shared'
import { $emit } from '@/js/includes/event-bus'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useStore } from '@/js/stores/index';
import { useUserStore } from '@/js/stores/user'
import { useNewsStore } from '@/js/stores/news'
import UserThumbnail from './user-thumbnail.vue'
import { authenticateOauth, processLoginError } from '@/js/includes/auth'
import PointsBlock from './points-block.vue'
import dayjs from 'dayjs'

const store = useStore()
const userStore = useUserStore()
const newsStore = useNewsStore()

const route = useRoute()
const router = useRouter()

// Computed
const t = computed(() => store.translations)
const connectionStatus = computed(() => userStore.connectionStatus)
const currentLanguage = computed(() => store.currentLanguage)
const loggedIn = computed(() => userStore.loggedIn)
const isMobile = computed(() => store.isMobile)
const settings = computed(() => store.settings)
const image = computed(() => userStore.userImage)
const firstName = computed(() => personNameFormalize(currentLanguage.value, userStore.userFirstName))
const lastName = computed(() => personNameFormalize(currentLanguage.value, userStore.userLastName))
const showLoginButtons = computed(() => store.settings.ui.layoutShowSignupButton)
const showMainMenu = computed(() => (isMobile.value && !route.meta.hideMainMenuMobile) || (!isMobile.value && !route.meta.hideMainMenuDesktop))
const numberOfMyRewards = computed(() => store.rewards?.length)
const menuBadge = computed(() => {
	// Check if we need to show rewards in badge
	let result = settings.value.ui.showUnusedRewardsBadge ? numberOfMyRewards.value : 0
	
	// Check if we have news in the side menu. If not, no need to add the cound to badge
	result += !includes(settings.value.ui.mainMenuOrderMobile, "news") ? newsStore.unreadCount : 0
	
	return result || ""
})

// Reactives
const location = window.location
const customerLoginUrl = computed(() => store.settings.customerLoginUrl)

// Methods
function openSideMenu(){ $emit('open-side-menu') }
function login(provider)
{
	authenticateOauth(provider)
		.then(() => $emit('login-successful'))
		.catch(e => processLoginError(e, { router, route }))
}

// Swedbank custom: banner rotation randomly based for mobile or web

const topBanner = computed(() => {
	// Static banner
	return {
		url: null,
		imageUrl: `/customer/images/top-banners/4-` + (isMobile.value ? 'mobile' : 'web') + '.gif',
		proportion: isMobile.value ? 31.67 : 13.88
	}

	// const age = dayjs().diff(userStore.birthday, 'y');
	
	// if(age >= 18){ // 18+ banners
	// 	return [
	// 		{
	// 			url: 'https://learn.investavimoakademija.lt/pages/is/',
	// 			imageUrl: isMobile.value ? '/customer/images/top-banners/Swedbank_akademija_2_mobile.jpg' : '/customer/images/top-banners/Swedbank_akademija_2_web.jpg',
	// 			proportion: isMobile.value ? 31.67 : 13.88
	// 		},
	// 		{
	// 			url: 'https://investavimozaidimas.lt/issukis',
	// 			imageUrl: isMobile.value ? '/customer/images/top-banners/Swedbank_akademija_mobile.jpg' : '/customer/images/top-banners/Swedbank_akademija_web.jpg',
	// 			proportion: isMobile.value ? 31.67 : 13.88
	// 		},
	// 	][getRandomInt(1)];
	// }

	// const bannerKey = getRandomInt(2) + 1
	// return {
	// 	url: (bannerKey != 3) ? 'https://blog.swedbank.lt/lengvai' : null,
	// 	imageUrl: `/customer/images/top-banners/${bannerKey}-` + (isMobile.value ? 'mobile' : 'web') + '.gif',
	// 	proportion: isMobile.value ? 31.67 : 13.88
	// }
})
function open(url){ if(url) window.open(url, "_blank") }

</script>